import React from 'react';
import { useTranslation } from 'react-i18next';
import { getPasswordRequirements } from '../../../utils/password-requirements';
import { IconCheck } from '../../app/icons/check';
import { cn } from '../../../utils/tailwind';
import { WebTooltip } from '../tooltip/web-tooltip';
import styles from './password-requirements.module.css';

interface PasswordRequirementsProps {
    password: string;
}

export const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({ password }) => {
    const { t } = useTranslation();
    const requirements = Object.entries(getPasswordRequirements(password));
    const missingRules = requirements.filter(([, met]) => !met);
    const completed = requirements.length - missingRules.length;
    const allMet = completed === requirements.length;
    const circumference = 2 * Math.PI * 17;
    const feedback = allMet
        ? t('dialogs.signUp.passwordRequirements.complete')
        : missingRules.map(([rule]) => t(`dialogs.signUp.passwordRequirements.missing.${rule}`)).join(' ');

    if (!password) return null;

    const checklist = (
        <ul className="m-0 grid list-none gap-[0.8rem] p-0 text-left text-[1.2rem] leading-[1.5]">
            {requirements.map(([rule, met]) => (
                <li key={rule} className="flex items-center gap-[0.8rem]">
                    <span
                        aria-hidden="true"
                        className={cn(
                            'flex h-[1.6rem] w-[1.6rem] shrink-0 items-center justify-center',
                            met ? 'text-[#bfe2cd]' : 'text-white/50',
                        )}
                    >
                        {met ? (
                            <IconCheck />
                        ) : (
                            <span className="h-[1rem] w-[1rem] rounded-full border border-current" />
                        )}
                    </span>
                    <span className="sr-only">
                        {t(`dialogs.signUp.passwordRequirements.${met ? 'met' : 'unmet'}`)}:{' '}
                    </span>
                    {t(`dialogs.signUp.passwordRequirements.${rule}`)}
                </li>
            ))}
        </ul>
    );

    return (
        <span className="mr-[0.8rem] inline-flex shrink-0">
            <WebTooltip content={checklist} position="top">
                <button
                    type="button"
                    data-password-feedback
                    data-password-complete={allMet}
                    aria-label={feedback}
                    className={cn(styles.indicator, allMet && styles.complete)}
                >
                    <svg
                        aria-hidden="true"
                        viewBox="0 0 40 40"
                        fill="none"
                        className={styles.ring}
                        style={{ color: `hsl(${Math.max(0, completed - 1) * 48} 38% 45%)` }}
                    >
                        <circle cx="20" cy="20" r="17" stroke="#d9cdc5" strokeWidth="2" />
                        <circle
                            cx="20"
                            cy="20"
                            r="17"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={circumference * (1 - Math.max(0.08, completed / 4))}
                            className={styles.progress}
                        />
                    </svg>
                    <span aria-hidden="true" className={styles.success}>
                        <IconCheck />
                        <span>{t('dialogs.signUp.passwordRequirements.strong')}</span>
                    </span>
                    <span className="sr-only" aria-live="polite">
                        {feedback}
                    </span>
                </button>
            </WebTooltip>
        </span>
    );
};
